import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts'
import { TrendingDown, TrendingUp, Minus } from 'lucide-react'
import { useGetStockAnalysisQuery } from '@/resources/apis/stock-analysis'
import type { StockAnalysisData, StockTrend } from '@/types/StockAnalysis'
import './stock-analysis-section.css'

interface StockAnalysisSectionProps {
  ticker: string
  data?: StockAnalysisData
}

function formatQuarter(isoDate: string): string {
  const [year, month] = isoDate.split('-')
  const quarter = Math.ceil(Number(month) / 3)
  return `T${quarter}/${year.slice(2)}`
}

function formatBRL(value: number | null): string {
  if (value === null) return '--'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

function formatPercent(value: number | null): string {
  if (value === null) return '--'
  return `${value.toFixed(2)}%`
}

const TREND_CONFIG: Record<StockTrend, { label: string; icon: typeof TrendingUp; className: string }> = {
  growing:  { label: 'Crescendo',  icon: TrendingUp,   className: 'stock-analysis__trend--growing' },
  stable:   { label: 'Estável',    icon: Minus,         className: 'stock-analysis__trend--stable' },
  declining:{ label: 'Em queda',   icon: TrendingDown,  className: 'stock-analysis__trend--declining' },
}

export function StockAnalysisSection({ ticker, data: externalData }: StockAnalysisSectionProps) {
  const { data: fetchedData, isLoading, isError } = useGetStockAnalysisQuery(ticker, !externalData)
  const data = externalData ?? fetchedData

  if (isLoading && !externalData) {
    return (
      <div className="market-details__card stock-analysis">
        <div className="market-details__card-header">
          <h2>Análise Trimestral</h2>
        </div>
        <div className="stock-analysis__loading">Carregando análise...</div>
      </div>
    )
  }

  if ((isError && !externalData) || !data) {
    return (
      <div className="market-details__card stock-analysis">
        <div className="market-details__card-header">
          <h2>Análise Trimestral</h2>
        </div>
        <div className="stock-analysis__empty">
          Análise ainda não disponível. Os dados são coletados periodicamente da CVM.
        </div>
      </div>
    )
  }

  const { financialStatements, analysis } = data

  const chartData = financialStatements.map((d) => ({
    quarter: formatQuarter(d.competence_quarter),
    'Receita (TTM)': d.net_revenue,
    'Lucro (TTM)': d.net_income,
  }))

  const latestQuarter = financialStatements.at(-1)
  const latestMargin =
    latestQuarter?.net_income != null && latestQuarter?.net_revenue
      ? (latestQuarter.net_income / latestQuarter.net_revenue) * 100
      : null
  const trend = analysis?.trend
  const trendConfig = trend ? TREND_CONFIG[trend] : null
  const TrendIcon = trendConfig?.icon

  return (
    <div className="market-details__card stock-analysis">
      <div className="market-details__card-header">
        <div>
          <h2>Análise Trimestral</h2>
          {latestQuarter && (
            <p>Referência: {formatQuarter(latestQuarter.competence_quarter)}</p>
          )}
        </div>
        {analysis && trendConfig && TrendIcon && (
          <div className={`stock-analysis__trend ${trendConfig.className}`}>
            <TrendIcon size={14} />
            <span>{trendConfig.label}</span>
          </div>
        )}
      </div>

      {/* Métricas do trimestre mais recente */}
      {latestQuarter && (
        <div className="stock-analysis__metrics">
          <div className="stock-analysis__metric">
            <span>Receita (TTM)</span>
            <strong>{formatBRL(latestQuarter.net_revenue)}</strong>
          </div>
          <div className="stock-analysis__metric">
            <span>Lucro (TTM)</span>
            <strong>{formatBRL(latestQuarter.net_income)}</strong>
          </div>
          <div className="stock-analysis__metric">
            <span>Margem líquida</span>
            <strong>{formatPercent(latestMargin)}</strong>
          </div>
          <div className="stock-analysis__metric">
            <span>Patrimônio Líquido</span>
            <strong>{formatBRL(latestQuarter.equity)}</strong>
          </div>
        </div>
      )}

      {/* Gráfico de evolução */}
      {chartData.length > 1 && (
        <div className="stock-analysis__chart">
          <p className="stock-analysis__chart-title">Evolução de Receita e Lucro (TTM)</p>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <XAxis dataKey="quarter" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => formatBRL(v)}
                width={72}
              />
              <Tooltip formatter={(v: number | undefined) => v != null ? formatBRL(v) : '--'} labelStyle={{ fontWeight: 600 }} />
              <Line
                type="monotone"
                dataKey="Receita (TTM)"
                stroke="var(--color-brand)"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="Lucro (TTM)"
                stroke="var(--color-success)"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Análise do Claude */}
      {analysis && (
        <div className="stock-analysis__ai">
          <div className="stock-analysis__ai-header">
            <div className="stock-analysis__score">
              <span className="stock-analysis__score-label">Score</span>
              <span
                className="stock-analysis__score-value"
                data-score={
                  analysis.health_score >= 7 ? 'high' : analysis.health_score >= 4 ? 'mid' : 'low'
                }
              >
                {analysis.health_score}/10
              </span>
            </div>
          </div>

          <p className="stock-analysis__summary">{analysis.summary}</p>

          <div className="stock-analysis__points">
            {analysis.positive_points.length > 0 && (
              <div className="stock-analysis__points-group">
                <p className="stock-analysis__points-title stock-analysis__points-title--positive">
                  Positivos
                </p>
                <ul>
                  {analysis.positive_points.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </div>
            )}
            {analysis.negative_points.length > 0 && (
              <div className="stock-analysis__points-group">
                <p className="stock-analysis__points-title stock-analysis__points-title--negative">
                  Atenção
                </p>
                <ul>
                  {analysis.negative_points.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <p className="stock-analysis__ai-footer">
            Gerado por IA · {new Date(analysis.generated_at).toLocaleDateString('pt-BR')}
          </p>
        </div>
      )}
    </div>
  )
}
